import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, X, Share2, Clock, Loader2, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";
import { createShare, createPairShare } from "../lib/api";
import { toast } from "sonner";

export const ShareModal = ({ open, onClose, reading, thread = [], mode = "solo", pairPayload = null }) => {
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || shareUrl || !reading) return;
    let cancelled = false;
    setLoading(true);

    const soloPayload = () => ({
      name: reading.name,
      date_of_birth: reading._meta?.date_of_birth,
      time_of_birth: reading._meta?.time_of_birth || null,
      place_of_birth: reading._meta?.place_of_birth || "",
      zodiac_sign: reading.zodiac_sign,
      zodiac_symbol: reading.zodiac_symbol,
      element: reading.element,
      ruling_planet: reading.ruling_planet,
      quality: reading.quality,
      cosmic_profile: reading.cosmic_profile,
      predictions: reading.predictions,
      lucky: reading.lucky,
      questions: thread.map((t) => ({
        question: t.question,
        answer: t.answer,
        insights: t.insights || [],
      })),
    });

    const promise =
      mode === "pair"
        ? createPairShare({
            ...pairPayload,
            questions: thread.map((t) => ({
              question: t.question,
              answer: t.answer,
              insights: t.insights || [],
            })),
          })
        : createShare(soloPayload());

    promise
      .then((res) => {
        if (cancelled) return;
        const prefix = mode === "pair" ? "/r/pair/" : "/r/";
        const url = `${window.location.origin}${prefix}${res.token}`;
        setShareUrl(url);
        setExpiresAt(res.expires_at);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) toast.error("Could not seal the link. Try again in a moment.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, reading, shareUrl, thread, mode, pairPayload]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy. Long-press the link to copy it manually.");
    }
  };

  const shareTitle = mode === "pair" && pairPayload
    ? `${pairPayload.person_a?.name} & ${pairPayload.person_b?.name} · Astrarium pair reading`
    : `${reading?.name}'s Astrarium reading`;
  const shareText = mode === "pair" && pairPayload
    ? `An Astrarium synastry reading for ${pairPayload.person_a?.name} and ${pairPayload.person_b?.name}`
    : `An astral almanac from Astrarium for ${reading?.name} (${reading?.zodiac_sign})`;

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (e) {
        if (e?.name !== "AbortError") console.error(e);
      }
    } else {
      handleCopy();
    }
  };

  const waUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText}:\n${shareUrl}`)}`;
  const twUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    mode === "pair" ? "An Astrarium synastry reading" : "An astral almanac from Astrarium"
  )}&url=${encodeURIComponent(shareUrl)}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
          data-testid="share-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-cosmic-800 border border-white/10 rounded-2xl p-6 sm:p-8 shadow-[0_24px_80px_rgba(0,0,0,0.6)]"
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="share-close"
              className="absolute top-4 right-4 text-white/40 hover:text-white p-1"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-2 mb-1">
              <Share2 className="h-4 w-4 text-stardust" />
              <p className="text-stardust/80 uppercase tracking-[0.3em] text-[10px]">
                {mode === "pair" ? "Share this pair reading" : "Share your almanac"}
              </p>
            </div>
            <h3 className="font-display text-2xl sm:text-3xl text-[#F5EBD8] mb-2">
              A private link, just for you<span className="text-stardust">.</span>
            </h3>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Anyone with the link can read this {mode === "pair" ? "pair reading" : "almanac"}{thread.length > 0 ? `, including your ${thread.length} question${thread.length === 1 ? "" : "s"} to the wheel,` : ""} for 7 days. Then it disappears from our records.
            </p>

            {loading && (
              <div className="flex items-center gap-3 text-slate-300 py-6 justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-stardust" />
                <span className="font-serif-italic">Sealing your reading&hellip;</span>
              </div>
            )}

            {!loading && shareUrl && (
              <>
                <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg p-2 mb-3">
                  <input
                    readOnly
                    value={shareUrl}
                    onFocus={(e) => e.target.select()}
                    data-testid="share-url-input"
                    className="flex-1 bg-transparent text-white text-sm px-2 py-2 outline-none truncate"
                  />
                  <Button
                    onClick={handleCopy}
                    data-testid="share-copy"
                    className="bg-stardust hover:bg-stardust-light text-cosmic-900 h-9 px-3 rounded-md transition-all"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>

                {expiresAt && (
                  <p className="flex items-center gap-2 text-[11px] text-slate-500 mb-5">
                    <Clock className="h-3 w-3" />
                    Expires {new Date(expiresAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                )}

                <div className="grid grid-cols-3 gap-2">
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="share-whatsapp"
                    className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] py-3 rounded-md bg-white/5 border border-white/10 text-slate-200 hover:border-stardust/50 hover:text-stardust transition-all"
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    WhatsApp
                  </a>
                  <a
                    href={twUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="share-twitter"
                    className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] py-3 rounded-md bg-white/5 border border-white/10 text-slate-200 hover:border-stardust/50 hover:text-stardust transition-all"
                  >
                    <span className="font-bold">X</span>
                  </a>
                  <button
                    type="button"
                    onClick={handleNativeShare}
                    data-testid="share-native"
                    className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] py-3 rounded-md bg-white/5 border border-white/10 text-slate-200 hover:border-stardust/50 hover:text-stardust transition-all"
                  >
                    <Share2 className="h-3.5 w-3.5" />
                    More
                  </button>
                </div>

                <p className="text-[11px] text-slate-500 mt-6 leading-relaxed">
                  This link includes the name and birth details you entered. Only share with
                  people you trust. <a href="/legal" className="underline text-slate-400 hover:text-stardust">Privacy &amp; legal</a>.
                </p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
